import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";

import "dayjs/locale/en";
import "dayjs/locale/ru";
import "dayjs/locale/uz-latn";

dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);

// ISO satr ham, ilovaning o'z `DD.MM.YYYY [HH:mm]` ko'rinishi ham qabul qilinadi.
const CUSTOM_FORMATS = ["DD.MM.YYYY HH:mm", "DD.MM.YYYY", "DD-MM-YYYY HH:mm", "DD-MM-YYYY"];

/**
 * i18n tili → dayjs lokali.
 *
 * dayjs dagi `uz` — kirill yozuvi, ilova esa lotinda yozadi, shuning uchun
 * `uz-latn`. Mintaqa qo'shilgan kodlar (`ru-RU`, `en-GB`) ham to'g'ri
 * tushishi uchun faqat birinchi bo'lak olinadi.
 */
const DAYJS_LOCALES: Record<string, string> = { uz: "uz-latn", ru: "ru", en: "en" };

const resolveLocale = (lang: string): string =>
  DAYJS_LOCALES[lang.toLowerCase().split("-")[0] ?? ""] ?? "en";

/**
 * Grafik bo'lagining kaliti (`YYYY-MM-DD`, kalendar sanasi) — tanlangan
 * tilda: kunlik grafikda "21 Sen", oylikda "Sen 2026".
 *
 * `Intl` EMAS: Chromium'ning yengil yig'ilmalari o'zbekcha ICU ma'lumotini
 * olib yurmaydi va o'qda "M09 21" chiqardi. dayjs lokallari ilova bilan
 * keladi. Kalit MAHALLIY sana sifatida o'qiladi — UTC dan orqadagi
 * mintaqada "1-sentabr" bo'lagi "31-avgust" bo'lib qolmaydi.
 */
export const formatChartBucket = (key: string, monthly: boolean, lang: string): string => {
  // To'liq ISO kalitda ham faqat kalendar qismi olinadi.
  const parsed = dayjs(key.slice(0, 10), "YYYY-MM-DD", true);
  if (!/^\d{4}-\d{2}-\d{2}/.test(key) || !parsed.isValid()) return key;
  return parsed.locale(resolveLocale(lang)).format(monthly ? "MMM YYYY" : "D MMM");
};

/**
 * Oy davri ("2026-08") — tanlangan tilda: "Avgust 2026", "август 2026",
 * "August 2026". `Intl` emas — o'zbekcha ICU bo'lmagan brauzerda
 * "2026 M08" chiqardi (`formatChartBucket` dagi sabab bilan bir xil).
 */
export const formatMonthPeriod = (period: string, lang: string): string => {
  const parsed = dayjs(period, "YYYY-MM", true);
  return parsed.isValid() ? parsed.locale(resolveLocale(lang)).format("MMMM YYYY") : period;
};

const parse = (dateString: string) => {
  const iso = dayjs(dateString);
  if (iso.isValid()) return iso;
  const custom = dayjs(dateString, CUSTOM_FORMATS);
  return custom.isValid() ? custom : null;
};

/**
 * Har qanday ISO yoki tahlil qilinadigan sanani dayjs orqali `dd-mm-yyyy HH:mm`
 * (24 soatlik) ko'rinishida yozadi. Tahlil qilib bo'lmasa, satrni o'zgarishsiz
 * qaytaradi.
 */
export const formatDateTime = (dateString: string): string => {
  const parsed = parse(dateString);
  return parsed ? parsed.format("DD-MM-YYYY HH:mm") : dateString;
};

/** Sanani `dd-mm-yyyy` ko'rinishida yozadi (faqat sana). */
export const formatDate = (dateString: string): string => {
  const parsed = parse(dateString);
  return parsed ? parsed.format("DD-MM-YYYY") : dateString;
};

/**
 * Tanlangan tildagi to'liq sana ("15 Mart 2024", "15 марта 2024 г.").
 *
 * Funksiya `lang` ni qabul qilar, lekin uni `void` qilib tashlab, hamma
 * joyda `DD-MM-YYYY HH:mm` qaytarardi — 34 ta chaqiruv joyi esa unga
 * `i18nLang` uzatib, tarjima qilingan sana kutardi.
 */
export const formatLongDate = (dateString: string, lang: string = "uz"): string => {
  const parsed = parse(dateString);
  return parsed ? parsed.locale(resolveLocale(lang)).format("LL") : dateString;
};
